import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Building2, ArrowLeft, Filter, Store, Mountain, Search } from 'lucide-react';
import { api } from '../services/api';
import Header from '../components/Header';
import Breadcrumbs from '../components/Breadcrumbs';
import { Badge } from '../components/ui/badge';
import { resolveBackendImage } from '../utils/brandLogo';


export default function DistritoPage() {
  const { municipioSlug, distritoSlug } = useParams();
  const [territory, setTerritory] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioSlug, distritoSlug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch territory info
      const territoryRes = await api.get(`/territories/by-slug/${municipioSlug}/${distritoSlug}`);
      setTerritory(territoryRes.data);

      // Fetch businesses in this territory
      const businessesRes = await api.get('/businesses', {
        params: {
          municipio: municipioSlug,
          territory_id: territoryRes.data.id,
          status: 'aprovado',
          limit: 100
        }
      });
      setBusinesses(businessesRes.data || []);

      // Fetch categories
      const categoriesRes = await api.get('/categories');
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar distrito:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBusinesses = businesses.filter(b => {
    const matchesCategory = !selectedCategory || b.categoria === selectedCategory;
    const matchesSearch = !searchTerm || 
      b.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      b.descricao_curta?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const breadcrumbItems = [
    { label: 'Início', path: '/' },
    { label: territory?.municipio?.nome || municipioSlug, path: `/${municipioSlug}` },
    { label: territory?.nome || distritoSlug }
  ];

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 py-8">
          <div className="container mx-auto px-4">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1,2,3].map(i => (
                  <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (!territory) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-gray-50 py-8">
          <div className="container mx-auto px-4 text-center">
            <Mountain className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h1 className="text-2xl font-bold mb-2">Distrito não encontrado</h1>
            <p className="text-gray-500 mb-4">O distrito que você está procurando não existe ou foi removido.</p>
            <Link to={`/${municipioSlug}`} className="text-[var(--primary)] hover:underline">
              ← Voltar para {municipioSlug}
            </Link>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50" data-testid="distrito-page">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-[#56007F] to-[#7B2D9E] text-white">
          <div className="container mx-auto px-4 py-8">
            <Breadcrumbs items={breadcrumbItems} className="mb-4 text-white/80" />
            
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                {territory.tipo === 'distrito' ? (
                  <Mountain className="w-10 h-10" />
                ) : (
                  <Building2 className="w-10 h-10" />
                )}
              </div>
              <div>
                <Badge variant="outline" className="mb-2 text-white border-white/50">
                  {territory.tipo === 'distrito' ? 'Distrito' : 'Sede'}
                </Badge>
                <h1 className="text-3xl font-bold">{territory.nome}</h1>
                <p className="text-white/80 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {territory.municipio?.nome}
                </p>
              </div>
            </div>

            {territory.descricao && (
              <p className="mt-4 text-white/90 max-w-2xl">
                {territory.descricao}
              </p>
            )}

            {/* Stats */}
            <div className="mt-6 flex gap-6">
              <div className="bg-white/10 rounded-lg px-4 py-2 backdrop-blur-sm">
                <p className="text-2xl font-bold">{territory.businesses_count || businesses.length}</p>
                <p className="text-sm text-white/70">Negócios</p>
              </div>
              {territory.children && territory.children.length > 0 && (
                <div className="bg-white/10 rounded-lg px-4 py-2 backdrop-blur-sm">
                  <p className="text-2xl font-bold">{territory.children.length}</p>
                  <p className="text-sm text-white/70">Bairros/Comunidades</p>
                </div>
              )}
              {territory.populacao_estimada && (
                <div className="bg-white/10 rounded-lg px-4 py-2 backdrop-blur-sm">
                  <p className="text-2xl font-bold">{territory.populacao_estimada.toLocaleString('pt-BR')}</p>
                  <p className="text-sm text-white/70">População</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bairros/Comunidades */}
        {territory.children && territory.children.length > 0 && (
          <div className="container mx-auto px-4 py-6">
            <h2 className="text-lg font-semibold mb-3">Bairros e Comunidades</h2>
            <div className="flex flex-wrap gap-2">
              {territory.children.map(child => (
                <Badge 
                  key={child.id} 
                  variant="outline"
                  className="cursor-pointer hover:bg-[var(--primary)]/10"
                >
                  {child.nome}
                  {child.tipo !== 'bairro' && (
                    <span className="ml-1 text-xs opacity-70">
                      ({child.tipo === 'comunidade' ? 'Comunidade' : child.tipo})
                    </span>
                  )}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="container mx-auto px-4 py-4">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar negócios..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg"
                  data-testid="search-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg"
                  data-testid="category-filter"
                >
                  <option value="">Todas as categorias</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nome}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Business List */}
        <div className="container mx-auto px-4 py-6">
          <h2 className="text-xl font-bold mb-4">
            Negócios em {territory.nome}
            <span className="text-gray-500 font-normal ml-2">
              ({filteredBusinesses.length} encontrados)
            </span>
          </h2>

          {filteredBusinesses.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl">
              <Store className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-lg font-medium">Nenhum negócio encontrado</p>
              <p className="text-gray-500">
                {searchTerm || selectedCategory
                  ? 'Tente ajustar os filtros de busca'
                  : `Seja o primeiro a cadastrar seu negócio em ${territory.nome}!`}
              </p>
              <Link 
                to={`/${municipioSlug}/cadastrar`}
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary-dark)]"
              >
                Cadastrar Negócio
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredBusinesses.map(business => (
                <Link
                  key={business.id}
                  to={`/${municipioSlug}/negocio/${business.id}`}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`business-card-${business.id}`}
                >
                  {business.imagem_capa ? (
                    <img 
                      src={resolveBackendImage(business.imagem_capa)} 
                      alt={business.nome}
                      className="w-full h-40 object-cover"
                    />
                  ) : (
                    <div className="w-full h-40 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      <Store className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{business.nome}</h3>
                    {business.descricao_curta && (
                      <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                        {business.descricao_curta}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <MapPin className="w-3 h-3" />
                      <span>{business.bairro}</span>
                    </div>
                    {business.categoria && (
                      <Badge variant="outline" className="mt-2">
                        {categories.find(c => c.id === business.categoria)?.nome || business.categoria}
                      </Badge>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Back Link */}
        <div className="container mx-auto px-4 py-8">
          <Link 
            to={`/${municipioSlug}`}
            className="inline-flex items-center gap-2 text-[var(--primary)] hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para {territory.municipio?.nome}
          </Link>
        </div>
      </main>
    </>
  );
}
